import { Component, computed, inject, input, InputSignal, Signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

import { SUPPORTED_LANGUAGES } from '@core/i18n/language';
import { LanguageService } from '@core/i18n/language.service';
import { writeStoredLanguage } from '@core/i18n/language-storage';
import { LandingHeader } from '@core/cms/landing.cms';
import { CmsButton, CmsMedia } from '@shared/models/cms.model';
import { CmsImage } from '@shared/ui/cms-image/cms-image';
import { CmsBtn } from '@shared/ui/cms-btn/cms-btn';
import { CmsStyles } from '@shared/directives/cms-styles/cms-styles.directive';

@Component({
    selector: 'app-header',
    imports: [
        RouterLink,
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        TranslatePipe,
        CmsImage,
        CmsBtn,
        CmsStyles,
    ],
    templateUrl: './header.html',
    styleUrl: './header.scss',
})
export class Header {
    private readonly _router: Router = inject(Router);
    private readonly _language: LanguageService = inject(LanguageService);

    /** CMS-provided header content (logo + navigation). */
    public readonly header: InputSignal<LandingHeader | null | undefined> = input<
        LandingHeader | null | undefined
    >();

    /** Active language code, used to build language-prefixed router links (`/en`, `/de`). */
    protected readonly lang: Signal<string> = this._language.lang;

    /** Logo media; when absent the bundled SVG is used as a fallback. */
    protected readonly logo: Signal<CmsMedia | null | undefined> = computed(
        (): CmsMedia | null | undefined => this.header()?.logo,
    );

    /** Primary navigation links, sourced from the CMS. */
    protected readonly navLinks: Signal<ReadonlyArray<CmsButton>> = computed(
        (): ReadonlyArray<CmsButton> => this.header()?.navLinks ?? [],
    );

    /** Languages offered by the switcher. */
    protected readonly languages: ReadonlyArray<string> = SUPPORTED_LANGUAGES;

    /** Saves the chosen language and navigates to the current page under its language prefix. */
    protected switchLanguage(language: string): void {
        writeStoredLanguage(language);

        const segments: ReadonlyArray<string> = this._router.url
            .split('?')[0]
            .split('/')
            .filter(Boolean);
        const rest: ReadonlyArray<string> = segments.slice(1);

        void this._router.navigate(['/', language, ...rest]);
    }
}
